import BaseParser from './BaseParser';
import _get from 'lodash/get';
import { valueUnitToString } from './util';
import FhirDateTime from './FhirDateTime';
import FhirArray from './FhirArray';
import Quantity from './Quantity';
import Range from './Range';
import Period from './Period';
import Primitive from './Primitive';
import { BaseParserOptions, TimingOptions } from './SharedTypes';

const timingLabels = {
  repeat: {
    id: 'displayValueParsers.timing.labels.repeat',
    defaultMessage: 'Repeat',
  },
  bounds: {
    id: 'displayValueParsers.timing.labels.bounds',
    defaultMessage: 'bounds',
  },
  count: {
    id: 'displayValueParsers.timing.labels.count',
    defaultMessage: 'repetitions',
  },
  countMax: {
    id: 'displayValueParsers.timing.labels.countMax',
    defaultMessage: 'max repetitions',
  },
  duration: {
    id: 'displayValueParsers.timing.labels.duration',
    defaultMessage: 'duration',
  },
  durationMax: {
    id: 'displayValueParsers.timing.labels.durationMax',
    defaultMessage: 'max duration',
  },
  frequency: {
    id: 'displayValueParsers.timing.labels.frequency',
    defaultMessage: 'frequency',
  },
  frequencyMax: {
    id: 'displayValueParsers.timing.labels.frequencyMax',
    defaultMessage: 'max frequency',
  },
  period: {
    id: 'displayValueParsers.timing.labels.period',
    defaultMessage: 'period',
  },
  periodMax: {
    id: 'displayValueParsers.timing.labels.periodMax',
    defaultMessage: 'max period',
  },
  when: {
    id: 'displayValueParsers.timing.labels.when',
    defaultMessage: 'when',
  },
};

type MessageDescriptor = {
  id: string;
  defaultMessage: string;
};

const getTimingLabel = (
  value: number | string,
  label: MessageDescriptor,
  formatMessage?: (msg: MessageDescriptor) => string,
) => {
  if (!value) return null;

  const formattedLabel = formatMessage
    ? formatMessage(label)
    : label.defaultMessage;
  return `${formattedLabel} - ${value}`;
};

class Timing extends BaseParser {
  private options: TimingOptions;

  /**
   * @param {Object} options.timing.dateTimeParser - Override the default dateTime parser for timing events
   * @param {Object} options.timing.quantityParser - Override the default quantity parser for repeat[].boundsDuration
   * @param {Object} options.timing.rangeParser - Override the default quantity parser for repeat[].boundsRange
   * @param {Object} options.timing.periodParser - Override the default period parser for repeat[].boundsPeriod
   * @param {Object} options.timing.primitiveParser - Override the default primitive parser for repeat primitive properties
   */
  constructor(options: BaseParserOptions = {}) {
    super();
    this.options = _get<BaseParserOptions, 'timing', TimingOptions>(
      options,
      'timing',
      {},
    );
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getDisplayValue(fhirProperty: fhir.Timing) {
    if (!fhirProperty) return null;

    const events = _get<fhir.Timing, 'event', fhir.dateTime[]>(
      fhirProperty,
      'event',
      [],
    );
    if (events.length !== 0) {
      const dateTimeParser = _get<
        TimingOptions,
        'dateTimeParser',
        FhirDateTime
      >(this.options, 'dateTimeParser', new FhirDateTime());
      const arrayParser = new FhirArray({
        fhirArray: {
          arrayElementParser: dateTimeParser,
          concatenate: true,
        },
      });

      return arrayParser.getDisplayValue(events);
    }

    const repeat = _get<fhir.Timing, 'repeat', fhir.TimingRepeat>(
      fhirProperty,
      'repeat',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
    if (repeat) {
      const quantityParser = _get<TimingOptions, 'quantityParser', Quantity>(
        this.options,
        'quantityParser',
        new Quantity(),
      );
      const rangeParser = _get<TimingOptions, 'rangeParser', Range>(
        this.options,
        'rangeParser',
        new Range(),
      );
      const periodParser = _get<TimingOptions, 'periodParser', Period>(
        this.options,
        'periodParser',
        new Period(),
      );
      const primitiveParser = _get<TimingOptions, 'primitiveParser', Primitive>(
        this.options,
        'primitiveParser',
        new Primitive(),
      );

      const {
        boundsDuration,
        boundsRange,
        boundsPeriod,
        count,
        countMax,
        duration,
        durationMax,
        durationUnit,
        frequency,
        frequencyMax,
        period: repeatPeriod,
        periodMax,
        periodUnit,
        when,
      } = repeat;

      const repParts = [];

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const countValue = primitiveParser.getDisplayValue(count);
      const countLabel = getTimingLabel(countValue, timingLabels.count);
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(countLabel);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const countMaxValue = primitiveParser.getDisplayValue(countMax);
      const countMaxLabel = getTimingLabel(
        countMaxValue,
        timingLabels.countMax,
      );
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(countMaxLabel);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const durationValue = primitiveParser.getDisplayValue(duration);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const durationUnitValue = primitiveParser.getDisplayValue(durationUnit);
      const durationWithUnit = valueUnitToString(
        durationValue,
        durationUnitValue,
      );
      const durationLabel = getTimingLabel(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        durationWithUnit,
        timingLabels.duration,
      );
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(durationLabel);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const durationMaxValue = primitiveParser.getDisplayValue(durationMax);
      const durationMaxUnitValue =
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        primitiveParser.getDisplayValue(durationUnit);
      const durationMaxWithUnit = valueUnitToString(
        durationMaxValue,
        durationMaxUnitValue,
      );
      const durationMaxLabel = getTimingLabel(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        durationMaxWithUnit,
        timingLabels.durationMax,
      );
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(durationMaxLabel);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const frequencyValue = primitiveParser.getDisplayValue(frequency);
      const frequencyLabel = getTimingLabel(
        frequencyValue,
        timingLabels.frequency,
      );
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(frequencyLabel);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const frequencyMaxValue = primitiveParser.getDisplayValue(frequencyMax);
      const frequencyMaxLabel = getTimingLabel(
        frequencyMaxValue,
        timingLabels.frequencyMax,
      );
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(frequencyMaxLabel);

      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      const repeatPeriodValue = primitiveParser.getDisplayValue(repeatPeriod);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const repeatPeriodUnitValue = primitiveParser.getDisplayValue(periodUnit);
      const periodWithUnit = valueUnitToString(
        repeatPeriodValue,
        repeatPeriodUnitValue,
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const periodLabel = getTimingLabel(periodWithUnit, timingLabels.period);
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(periodLabel);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const periodMaxValue = primitiveParser.getDisplayValue(periodMax);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const periodUnitValue = primitiveParser.getDisplayValue(periodUnit);
      const periodMaxWithUnit = valueUnitToString(
        periodMaxValue,
        periodUnitValue,
      );
      const periodMaxLabel = getTimingLabel(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        periodMaxWithUnit,
        timingLabels.periodMax,
      );
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(periodMaxLabel);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const whenValue = primitiveParser.getDisplayValue(when);
      const whenLabel = getTimingLabel(whenValue, timingLabels.when);
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(whenLabel);

      const boundsValue =
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        quantityParser.getDisplayValue(boundsDuration) ||
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        rangeParser.getDisplayValue(boundsRange) ||
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        periodParser.getDisplayValue(boundsPeriod);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const boundsLabel = getTimingLabel(boundsValue, timingLabels.bounds);
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      repParts.push(boundsLabel);

      const nonEmptyRepParts = repParts.filter(part => !!part);
      if (nonEmptyRepParts.length === 0) return null;

      const prefix = timingLabels.repeat.defaultMessage;
      return `${prefix}: ${nonEmptyRepParts.join('; ')}`;
    }

    return null;
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getSortValue(fhirProperty: fhir.Timing) {
    if (!fhirProperty) return null;

    const events = _get<fhir.Timing, 'event', fhir.dateTime[]>(
      fhirProperty,
      'event',
      [],
    );
    if (events.length !== 0) {
      const dateTimeParser = _get<
        TimingOptions,
        'dateTimeParser',
        FhirDateTime
      >(this.options, 'dateTimeParser', new FhirDateTime());
      const arrayParser = new FhirArray({
        fhirArray: {
          arrayElementParser: dateTimeParser,
          concatenate: true,
        },
      });

      return arrayParser.getSortValue(events);
    } else {
      // if no events, return display value
      return this.getDisplayValue(fhirProperty);
    }
  }
}

export default Timing;
